import React from 'react';
import { useSelector } from 'react-redux';
import { Provider, useAtomValue } from 'jotai';
import { SummaryContainer } from '@components/summary-panel';
import { MetricsChartContainer as HzMetricsChartContainer } from '@components/metrics-panel-hz';
import { ErrorBoundary, PageTitle, Footer } from '@components/common';
import { SAVE_SERVICE_DASHBOARD_KEYS } from '@utils/constants';
import RetryContext from '@src/contexts/retryContext';
import { ContentWrapper, SectionWrapper } from './styled';
import DimensionContainer from '@components/dimension-panel-hz';
import MetricFilterBar from '@components/summary-panel/MetricFilterBar';
import WidgetPanelContainer from '@components/top-widget-panel';
import { intervalSelector, isCDNAccountSelector } from '@store/common/selectors';
import AlertPanel from '@components/alert-panel';
import ZenPanel from '@components/zen';
import { buildDashboardStore } from '@src/atoms/utils';
import CompareFiltersBar from '@components/summary-panel/CompareFiltersBar';
import { checkFeature } from '@utils/platform/features/checkFeature';
import { horizontalLayoutAtom } from '@src/components/metrics-panel-hz/atoms';
import ActionToolbar from '@src/components/ActionToolbar';
import usePageMeta from '@src/hooks/usePageMeta';
import { usePersistAtoms } from '@hooks';
import dataToPersistSelector from '@src/store/common/persistence';
import useApplyAtomPersistence from '@src/hooks/useApplyAtomPersistence';
import { useParams } from 'react-router-dom';
import useKPI from '@src/hooks/useKPI';
import KPISettingsDrawer from '@components/kpi-settings-drawer';

const defaultStore = buildDashboardStore();

function Main({ title = 'Trends', id }) {
  const interval = useSelector(intervalSelector);
  const isCDNAccount = useSelector(isCDNAccountSelector);
  const horizontalLayout = useAtomValue(horizontalLayoutAtom);

  // Initialize KPI within the scoped Jotai Provider
  useKPI();
  usePersistAtoms({ bindSelector: dataToPersistSelector });

  return (
    <RetryContext.Provider value={{ interval }}>
      <ErrorBoundary>
        <PageTitle text={title} />
        <SummaryContainer id={id} />
        <CompareFiltersBar />
        <MetricFilterBar />
        <AlertPanel />
        <ContentWrapper horizontalLayout={horizontalLayout}>
          <ActionToolbar />
          {checkFeature('trends.sections.summary', !isCDNAccount) ? <WidgetPanelContainer /> : null}
          <SectionWrapper horizontalLayout={horizontalLayout}>
            <HzMetricsChartContainer />
            <DimensionContainer />
            <ZenPanel />
          </SectionWrapper>
          <Footer />
        </ContentWrapper>
        <KPISettingsDrawer />
      </ErrorBoundary>
    </RetryContext.Provider>
  );
}

const WrappedMain = (props) => {
  const { store = defaultStore }: any = props;
  const { id = SAVE_SERVICE_DASHBOARD_KEYS.TRENDS } = useParams(); // default home page is trends

  usePageMeta({ id, store });

  useApplyAtomPersistence({ pageProps: props, store });

  return (
    <Provider store={store}>
      <Main {...props} id={id} />
    </Provider>
  );
};

export default WrappedMain;
